/**
 * Public /wage-calculator page.
 *
 * No auth. Users land, type their last few months of P&L into the
 * yellow cells, tweak settings in the right-column settings panel, and
 * see a plain-English recommended owner wage update live. The actual
 * math runs on the backend via `/api/public/wage-calculator/compute`.
 *
 * File upload UI is scaffolded as a Phase D stub — "Browse files" opens
 * a toast until parsing lands.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { api, loadAuthTokenFromStorage } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const TRIAL_TOKEN_KEY = "nzt_wage_token";
const CLIENT_AUTH_KEY = "co_token";

function _authHeader() {
    const t = localStorage.getItem(CLIENT_AUTH_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
}

/* -------- Brand palette (NZ Taxation Limited) -------- */
const NAVY = "#1F3864";
const GREEN = "#2E9E57";
const GREEN_LIGHT = "#43C067";
const BG = "#EEF2F8";
const CELL = "#FFF7CF";
const CELL_TEXT = "#1F3864";

const MONTHS = 6;
const EXPENSE_ROWS = [
    { key: "subcontractors",    label: "Subcontractors / direct labour" },
    { key: "staff_wages",       label: "Staff wages & PAYE (excl. owner)" },
    { key: "software",          label: "Software & subscriptions" },
    { key: "advertising",       label: "Advertising & marketing" },
    { key: "rent",              label: "Rent & premises" },
    { key: "insurance",         label: "Insurance" },
    { key: "bank_fees",         label: "Bank, payment & merchant fees" },
    { key: "phone_internet",    label: "Phone & internet" },
    { key: "travel",            label: "Travel" },
    { key: "other_expenses",    label: "Other operating expenses" },
];
const INCOME_ROWS = [
    { key: "sales", label: "Sales / service fees" },
    { key: "other_income", label: "Other income" },
];

const DEFAULT_SETTINGS = {
    hours_per_week: 40,
    market_hourly_rate: 52.0,
    retained_profit_pct: 0.20,
    employer_oncost_pct: 0.035,
    pay_weeks_per_year: 52,
    avg_monthly_fee_per_client: "",
    cash_at_bank: "",
    gst_owing: "",
    paye_owing: "",
    income_tax_owing: "",
    min_cash_buffer: "",
};

function nz(v) {
    const n = Number(v || 0);
    return `$${n.toLocaleString("en-NZ", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
function nz2(v) {
    const n = Number(v || 0);
    return `$${n.toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function emptyMonth() { return Object.fromEntries(INCOME_ROWS.concat(EXPENSE_ROWS).map((r) => [r.key, ""])); }
function toNumber(v) { if (v === "" || v == null) return 0; const n = Number(v); return Number.isFinite(n) ? n : 0; }

export default function WageCalculator() {
    const [months, setMonths] = useState(() => Array.from({ length: MONTHS }, emptyMonth));
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [confirmed, setConfirmed] = useState(false);
    const [result, setResult] = useState(null);
    const [computing, setComputing] = useState(false);
    const [monthLabels, setMonthLabels] = useState([]);
    const [ownerPayLines, setOwnerPayLines] = useState([]);
    const [unmatchedLines, setUnmatchedLines] = useState([]);
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [uploading, setUploading] = useState(false);
    const [trialStatus, setTrialStatus] = useState(null);     // { status, days_left, name } once verified
    const [trialDialogOpen, setTrialDialogOpen] = useState(false);
    const [syncSource, setSyncSource] = useState(null);        // { available, source, last_synced_at, org_name } for logged-in clients
    const [syncing, setSyncing] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const debounceRef = useRef(null);

    // Logged-in client? probe the sync source once.
    useEffect(() => {
        loadAuthTokenFromStorage();          // hydrate the shared axios client auth header
        if (!localStorage.getItem(CLIENT_AUTH_KEY)) return;
        (async () => {
            try {
                const { data } = await api.get("/portal/wage-calculator/sync/status");
                if (data?.available) setSyncSource(data);
            } catch { /* not a client — fine, stay on public mode */ }
        })();
    }, []);

    // Verification handling — ?token=xxx in URL after email click
    useEffect(() => {
        const token = searchParams.get("token") || localStorage.getItem(TRIAL_TOKEN_KEY);
        if (!token) return;
        (async () => {
            try {
                const path = searchParams.get("token") ? "/trial/verify" : "/trial/status";
                const { data } = await axios.get(`${API}/api/public/wage-calculator${path}?token=${token}`);
                setTrialStatus(data);
                if (data.token || searchParams.get("token")) {
                    localStorage.setItem(TRIAL_TOKEN_KEY, data.token || token);
                    toast.success(data.status === "converted" ? "Welcome back — you're a client now." : "Email verified — your 30 days starts now.");
                    const p = new URLSearchParams(searchParams); p.delete("token"); setSearchParams(p, { replace: true });
                }
            } catch (e) {
                if (searchParams.get("token")) {
                    toast.error("That verification link is invalid or already used.");
                    const p = new URLSearchParams(searchParams); p.delete("token"); setSearchParams(p, { replace: true });
                }
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Live compute — debounced so the server isn't hit on every keystroke
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setComputing(true);
            try {
                const payload = {
                    months: months.map((m) => Object.fromEntries(Object.entries(m).map(([k, v]) => [k, toNumber(v)]))),
                    hours_per_week: toNumber(settings.hours_per_week) || 40,
                    market_hourly_rate: toNumber(settings.market_hourly_rate) || 52,
                    retained_profit_pct: toNumber(settings.retained_profit_pct) || 0,
                    employer_oncost_pct: toNumber(settings.employer_oncost_pct) || 0,
                    pay_weeks_per_year: toNumber(settings.pay_weeks_per_year) || 52,
                    avg_monthly_fee_per_client: settings.avg_monthly_fee_per_client === "" ? null : toNumber(settings.avg_monthly_fee_per_client),
                    cash_at_bank: settings.cash_at_bank === "" ? null : toNumber(settings.cash_at_bank),
                    gst_owing: settings.gst_owing === "" ? null : toNumber(settings.gst_owing),
                    paye_owing: settings.paye_owing === "" ? null : toNumber(settings.paye_owing),
                    income_tax_owing: settings.income_tax_owing === "" ? null : toNumber(settings.income_tax_owing),
                    min_cash_buffer: settings.min_cash_buffer === "" ? null : toNumber(settings.min_cash_buffer),
                    owner_pay_excluded_confirmed: confirmed,
                };
                const { data } = await axios.post(`${API}/api/public/wage-calculator/compute`, payload);
                setResult(data);
            } catch { /* keep previous */ }
            finally { setComputing(false); }
        }, 350);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [months, settings, confirmed]);

    const monthlyNetProfit = useMemo(() => months.map((m) => {
        const inc = INCOME_ROWS.reduce((s, r) => s + toNumber(m[r.key]), 0);
        const exp = EXPENSE_ROWS.reduce((s, r) => s + toNumber(m[r.key]), 0);
        return inc - exp;
    }), [months]);

    function patchMonth(idx, key, value) {
        setMonths((prev) => prev.map((m, i) => i === idx ? { ...m, [key]: value } : m));
    }
    function patchSetting(key, value) {
        setSettings((s) => ({ ...s, [key]: value }));
    }

    async function handleUpload(file) {
        if (!file) return;
        const okExt = /\.(xlsx|xls|csv|pdf)$/i.test(file.name);
        if (!okExt) { toast.error("File must be .xlsx, .xls, .csv or .pdf"); return; }
        if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10 MB)"); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await axios.post(`${API}/api/public/wage-calculator/parse`, fd);
            applyParsedGrid(data, file.name);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not read that file — type numbers manually below.";
            toast.error(msg);
        } finally {
            setUploading(false);
        }
    }

    function applyParsedGrid(data, label) {
        const parsed = data.months || [];
        const next = Array.from({ length: MONTHS }, emptyMonth);
        parsed.slice(0, MONTHS).forEach((m, i) => {
            INCOME_ROWS.concat(EXPENSE_ROWS).forEach((r) => { next[i][r.key] = m[r.key] || ""; });
        });
        setMonths(next);
        setMonthLabels(parsed.map((m) => m.label));
        setOwnerPayLines(data.owner_pay_lines || []);
        setUnmatchedLines(data.unmatched_lines || []);
        setUploadedFileName(label);
        toast.success(`Loaded ${parsed.length} month${parsed.length === 1 ? "" : "s"} from ${label}`);
    }

    async function pullFromIntegration(monthsBack = 4) {
        if (!syncSource?.available) return;
        setSyncing(true);
        try {
            const { data } = await api.get(`/portal/wage-calculator/sync?months_back=${monthsBack}`);
            applyParsedGrid(data, `${(syncSource.source || "integration").toUpperCase()} · ${monthsBack} mo`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || `Couldn't pull from ${syncSource.source} — use a file instead.`);
        } finally { setSyncing(false); }
    }

    return (
        <div style={{ background: BG }} className="min-h-screen text-[#0F1115]" data-testid="wage-calculator-page">
            <TopBanner onStartTrial={() => setTrialDialogOpen(true)} trialStatus={trialStatus} />
            <TopNav />
            {trialStatus && (trialStatus.status === "active" || trialStatus.status === "expiring") && (
                <div className="bg-emerald-50 border-b border-emerald-200 text-emerald-900 text-[12.5px] px-4 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="wc-trial-banner">
                    <span>✓ Free 30-day access <b>active</b>{trialStatus.name ? ` for ${trialStatus.name}` : ""} — <b>{trialStatus.days_left}</b> day{trialStatus.days_left === 1 ? "" : "s"} left.</span>
                    <button onClick={() => { localStorage.removeItem(TRIAL_TOKEN_KEY); setTrialStatus(null); toast.info("Signed out of trial on this browser."); }} className="underline text-[11.5px]" data-testid="wc-trial-signout">Sign out of trial</button>
                </div>
            )}
            {trialStatus && trialStatus.status === "expired" && (
                <div className="bg-red-50 border-b border-red-200 text-red-900 text-[12.5px] px-4 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="wc-trial-expired">
                    <span>⚠ Your free access has ended. Become an NZ Taxation client to keep using the Owner Wage Calculator.</span>
                    <a href="https://www.nztaxation.nz" target="_blank" rel="noreferrer" className="underline font-semibold">Talk to us →</a>
                </div>
            )}
            <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-10 pb-16">
                <h1 className="text-3xl sm:text-4xl font-bold" style={{ color: NAVY }}>How much can you safely pay yourself?</h1>
                <p className="mt-2 text-[15px] text-[#4A5160]">Enter your first few months of profit &amp; loss. Get your recommended owner wage in plain English.</p>

                <div className="mt-7 grid gap-5 lg:grid-cols-[1.35fr_1fr]">
                    {/* LEFT — form */}
                    <div className="space-y-5">
                        {syncSource?.available && (
                            <SyncBadge syncSource={syncSource} onPull={pullFromIntegration} syncing={syncing} />
                        )}
                        <UploadCard
                            onUpload={handleUpload}
                            uploading={uploading}
                            uploadedFileName={uploadedFileName}
                            monthLabels={monthLabels}
                            ownerPayLines={ownerPayLines}
                            unmatchedLines={unmatchedLines}
                        />
                        <GridCard
                            months={months}
                            monthLabels={monthLabels}
                            monthlyNetProfit={monthlyNetProfit}
                            onCell={patchMonth}
                            confirmed={confirmed}
                            setConfirmed={setConfirmed}
                        />
                        <SettingsCard settings={settings} patch={patchSetting} />
                    </div>

                    {/* RIGHT — results */}
                    <div className="space-y-5 lg:sticky lg:top-6 self-start">
                        <RecommendedCard result={result} blurred={!confirmed} computing={computing} />
                        <AffordVsFairCard result={result} onStartTrial={() => setTrialDialogOpen(true)} trialStatus={trialStatus} />
                    </div>
                </div>
            </main>
            <FooterTrust />
            {trialDialogOpen && <TrialStartDialog onClose={() => setTrialDialogOpen(false)} />}
        </div>
    );
}

/* -------- Top gradient banner + nav -------- */
function TopBanner({ onStartTrial, trialStatus }) {
    const active = trialStatus && (trialStatus.status === "active" || trialStatus.status === "expiring");
    return (
        <div
            className="flex items-center justify-between gap-4 px-4 sm:px-6 lg:px-10 py-2.5 text-white"
            style={{ background: `linear-gradient(90deg, ${NAVY} 0%, ${GREEN} 100%)` }}
            data-testid="wc-banner"
        >
            <div className="text-[13px] sm:text-[14px]">
                <b>Start your FREE 30-day access</b> — from NZ Taxation Limited · <span className="opacity-90">no credit card</span>
            </div>
            <button
                onClick={onStartTrial}
                disabled={active}
                className="rounded-md px-3 py-1.5 text-[13px] font-semibold text-white shadow disabled:opacity-70 disabled:cursor-not-allowed"
                style={{ background: GREEN_LIGHT }}
                data-testid="wc-banner-cta"
            >
                {active ? `Trial active · ${trialStatus.days_left}d left` : "Start Free Trial"}
            </button>
        </div>
    );
}

function BrandMark() {
    return (
        <Link to="/wage-calculator" className="flex items-center gap-2" data-testid="wc-brand">
            <span className="grid place-items-center h-9 w-9 rounded-md text-white font-bold text-[14px] tracking-tight" style={{ background: NAVY }}>NZ</span>
            <span className="font-semibold text-[18px]" style={{ color: NAVY }}>
                Taxation <span style={{ color: GREEN }}>Limited</span>
            </span>
        </Link>
    );
}

function TopNav() {
    const link = "text-[13.5px] text-[#334155] hover:text-[#0F1115] transition-colors";
    return (
        <nav className="bg-white border-b border-[#E4E8F0]" data-testid="wc-nav">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <BrandMark />
                <div className="hidden md:flex items-center gap-6">
                    <a href="#calc" className={link} data-testid="wc-nav-owner">Owner Wage Calculator</a>
                    <a href="#services" className={link}>Our Services</a>
                    <a href="#about" className={link}>About</a>
                    <a href="#contact" className={link}>Contact</a>
                    <Link to="/login" className="rounded-md px-3 py-1.5 text-[13px] font-semibold text-white" style={{ background: NAVY }} data-testid="wc-nav-login">
                        Client Login
                    </Link>
                </div>
            </div>
        </nav>
    );
}

/* -------- STEP 1 — upload card (functional) -------- */
function UploadCard({ onUpload, uploading, uploadedFileName, monthLabels, ownerPayLines, unmatchedLines }) {
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    return (
        <section id="calc" className="rounded-2xl bg-white shadow-sm border border-[#E4E8F0] p-5">
            <h2 className="text-[16px] font-bold" style={{ color: NAVY }}>1. Upload your monthly P&amp;L</h2>
            <input ref={inputRef} type="file" className="hidden" accept=".xlsx,.xls,.csv,.pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) onUpload(f); }}
                onClick={() => inputRef.current?.click()}
                className="mt-3 rounded-xl border-2 border-dashed flex items-center gap-4 p-5 cursor-pointer transition-colors"
                style={{ borderColor: dragOver ? GREEN : "#B9C6DD", background: dragOver ? "#F0FBF4" : "#F8FBFF" }}
                data-testid="wc-upload-zone"
            >
                <div className="grid place-items-center h-12 w-12 rounded-full shrink-0" style={{ background: "#E6F4EC", color: GREEN }}>
                    <UploadIcon />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-semibold" style={{ color: NAVY }}>Drag &amp; drop your monthly P&amp;L here</div>
                    <div className="text-[12.5px] text-[#5E636E] mt-0.5">Excel or PDF from Xero, MYOB or your accountant. 3-6 months works best.</div>
                    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                        {[".XLSX", ".XLS", ".CSV", ".PDF"].map((c) => (
                            <span key={c} className="rounded border border-[#CBD5E1] bg-white px-2 py-0.5 text-[10.5px] font-semibold text-[#334155]">{c}</span>
                        ))}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                    className="rounded-md px-4 py-2 text-[13px] font-semibold text-white"
                    style={{ background: NAVY }}
                    data-testid="wc-browse-files"
                    disabled={uploading}
                >
                    {uploading ? "Reading…" : "Browse files"}
                </button>
            </div>

            {uploadedFileName && (
                <div className="mt-3 flex items-center gap-3 rounded-md border border-[#CBD5E1] bg-[#F8FBFF] px-3 py-2" data-testid="wc-upload-row">
                    <span className="rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5">
                        {uploadedFileName.split(".").pop().toUpperCase()}
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="truncate text-[13px] font-medium" style={{ color: NAVY }}>{uploadedFileName}</div>
                        <div className="text-[11px] text-emerald-700">✓ Read successfully · {monthLabels.length} month{monthLabels.length === 1 ? "" : "s"} found</div>
                    </div>
                    <div className="h-1.5 w-32 bg-emerald-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: "100%" }} />
                    </div>
                </div>
            )}

            {ownerPayLines?.length > 0 && (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900" data-testid="wc-owner-pay">
                    <div className="font-semibold">⚠ Owner pay lines excluded ({ownerPayLines.length})</div>
                    <ul className="mt-1 text-[12px] list-disc list-inside">
                        {ownerPayLines.map((p) => <li key={p.label}><b>{p.label}</b> — ${p.monthly.reduce((s, v) => s + Number(v || 0), 0).toLocaleString("en-NZ")} total</li>)}
                    </ul>
                </div>
            )}
            {unmatchedLines?.length > 0 && (
                <div className="mt-2 rounded-md border border-[#CBD5E1] bg-[#F8FBFF] px-3 py-2 text-[12px] text-[#334155]" data-testid="wc-unmatched">
                    <div className="font-semibold" style={{ color: NAVY }}>⓵ Added to "Other operating expenses":</div>
                    <ul className="mt-1 list-disc list-inside">
                        {unmatchedLines.map((p) => <li key={p.label}>{p.label}</li>)}
                    </ul>
                </div>
            )}

            <div className="mt-4 text-center text-[11px] text-[#5E636E] uppercase tracking-wider">— or type your numbers manually —</div>
        </section>
    );
}

/* -------- STEP 2 — grid -------- */
function GridCard({ months, monthLabels, monthlyNetProfit, onCell, confirmed, setConfirmed }) {
    return (
        <section className="rounded-2xl bg-white shadow-sm border border-[#E4E8F0] p-5">
            <h2 className="text-[16px] font-bold" style={{ color: NAVY }}>2. Check the numbers we read (edit if needed)</h2>
            <div className="mt-3 overflow-x-auto">
                <table className="w-full text-[13px] border-separate" style={{ borderSpacing: "4px" }} data-testid="wc-grid">
                    <thead>
                        <tr>
                            <th className="text-left"></th>
                            {months.map((_, i) => (
                                <th key={i} className="text-white font-semibold text-[12px] px-3 py-2 rounded-md" style={{ background: NAVY, minWidth: 92 }}>
                                    {monthLabels?.[i] || `Month ${i + 1}`}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {INCOME_ROWS.concat(EXPENSE_ROWS).map((row) => (
                            <tr key={row.key}>
                                <td className="pr-3 text-[13px] text-[#1E293B] whitespace-nowrap">{row.label}</td>
                                {months.map((m, i) => (
                                    <td key={i}>
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            value={m[row.key]}
                                            onChange={(e) => onCell(i, row.key, e.target.value)}
                                            placeholder="$0"
                                            className="w-full rounded-md border px-2 py-1.5 text-right text-[13px] font-medium outline-none focus:ring-2 focus:ring-[#1F3864]"
                                            style={{ background: CELL, color: CELL_TEXT, borderColor: "#F3E8A2" }}
                                            data-testid={`wc-cell-${row.key}-${i}`}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                        <tr>
                            <td className="pr-3 text-[13px] font-bold" style={{ color: NAVY }}>Net profit before owner pay</td>
                            {monthlyNetProfit.map((v, i) => (
                                <td key={i}>
                                    <div
                                        className="w-full rounded-md px-2 py-1.5 text-right text-[13px] font-bold"
                                        style={{ background: "#DFEBFF", color: NAVY, border: "1px solid #BFD7FF" }}
                                        data-testid={`wc-net-${i}`}
                                    >
                                        {nz(v)}
                                    </div>
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>
            <label className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[12.5px] text-amber-900 cursor-pointer" data-testid="wc-confirm">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5" />
                <span>⚠ Owner wages or drawings found in the file will be excluded from expenses. Please confirm.</span>
            </label>
        </section>
    );
}

/* -------- STEP 3 — settings -------- */
function SettingsCard({ settings, patch }) {
    const Row = ({ label, k, pct, placeholder = "" }) => (
        <div className="flex items-center justify-between gap-3 py-1">
            <label className="text-[13px] text-[#1E293B]">{label}</label>
            <div className="relative w-40">
                <input
                    type="number"
                    step={pct ? "0.5" : "0.01"}
                    value={pct ? (settings[k] === "" ? "" : Math.round(Number(settings[k]) * 10000) / 100) : settings[k]}
                    onChange={(e) => patch(k, pct ? (e.target.value === "" ? "" : Number(e.target.value) / 100) : e.target.value)}
                    placeholder={placeholder}
                    className="w-full rounded-md border px-2 py-1.5 text-right text-[13px] font-semibold outline-none focus:ring-2 focus:ring-[#1F3864]"
                    style={{ background: CELL, color: CELL_TEXT, borderColor: "#F3E8A2" }}
                    data-testid={`wc-set-${k}`}
                />
                {pct && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] font-semibold" style={{ color: CELL_TEXT }}>%</span>}
            </div>
        </div>
    );
    return (
        <section className="rounded-2xl bg-white shadow-sm border border-[#E4E8F0] p-5">
            <h2 className="text-[16px] font-bold" style={{ color: NAVY }}>3. Your settings</h2>
            <div className="mt-3 grid gap-x-10 gap-y-1 md:grid-cols-2">
                <Row label="Hours worked per week" k="hours_per_week" />
                <Row label="Retained profit" k="retained_profit_pct" pct />
                <Row label="Market hourly rate" k="market_hourly_rate" />
                <Row label="Employer on-cost" k="employer_oncost_pct" pct />
                <Row label="Pay weeks per year" k="pay_weeks_per_year" />
                <Row label="Average monthly fee per client" k="avg_monthly_fee_per_client" placeholder="optional" />
            </div>
            <div className="mt-4 pt-3 border-t border-[#EEF2F8]">
                <div className="text-[12.5px] font-semibold" style={{ color: NAVY }}>Optional cash check</div>
                <div className="mt-2 grid gap-x-10 gap-y-1 md:grid-cols-2">
                    <Row label="Cash at bank" k="cash_at_bank" placeholder="optional" />
                    <Row label="GST owing" k="gst_owing" placeholder="optional" />
                    <Row label="PAYE owing" k="paye_owing" placeholder="optional" />
                    <Row label="Income tax owing" k="income_tax_owing" placeholder="optional" />
                    <Row label="Minimum cash buffer" k="min_cash_buffer" placeholder="optional" />
                </div>
            </div>
        </section>
    );
}

/* -------- RIGHT — Recommended -------- */
function RecommendedCard({ result, blurred, computing }) {
    const limiting = result?.limiting_factor === "PROFIT" ? "Limited by profit" : "Limited by market rate";
    const confidence = result?.confidence || "Low";
    const cash = result?.cash_status || "Optional, not completed";
    return (
        <section className="rounded-2xl shadow-sm border p-5 text-white" style={{ background: GREEN, borderColor: GREEN }} data-testid="wc-recommended">
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="text-[14px] font-semibold opacity-95">Your recommended owner wage</div>
                    <div className="text-[11px] opacity-80">(gross, before tax)</div>
                </div>
                {computing && <div className="text-[10px] opacity-80 animate-pulse">updating…</div>}
            </div>
            <div className={`mt-3 ${blurred ? "blur-sm select-none" : ""} transition`}>
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="text-[46px] leading-none font-extrabold tracking-tight">${Number(result?.recommended_weekly || 0).toLocaleString("en-NZ", { maximumFractionDigits: 0 })}</div>
                    <div className="pb-1 text-[14px] opacity-90">per week</div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                    {[
                        { label: "per month", val: nz(result?.recommended_monthly) },
                        { label: "per year", val: nz(result?.recommended_annual) },
                        { label: "per hour worked", val: nz2(result?.recommended_hourly) },
                    ].map((c) => (
                        <div key={c.label} className="rounded-xl bg-white/95 text-[#0F1115] px-3 py-2 text-center shadow-sm">
                            <div className="text-[18px] font-bold" style={{ color: NAVY }}>{c.val}</div>
                            <div className="text-[10.5px] text-[#5E636E] mt-0.5">{c.label}</div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
                <Chip label={limiting} bg="#FFF4C7" fg={NAVY} testid="wc-chip-limit" />
                <Chip label={`Confidence: ${confidence}`} bg="#E7F2E9" fg="#2E6F3A" testid="wc-chip-confidence" />
                <Chip label={`Cash status: ${cash}`} bg="#FFFFFF" fg={NAVY} testid="wc-chip-cash" />
            </div>
            {blurred && (
                <div className="mt-4 flex items-start gap-2 rounded-md bg-white/15 border border-white/30 px-3 py-2 text-[12px]">
                    <span>👆</span>
                    <span>Tick the confirmation above to reveal your number.</span>
                </div>
            )}
        </section>
    );
}

function Chip({ label, bg, fg, testid }) {
    return (
        <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold" style={{ background: bg, color: fg }} data-testid={testid}>
            {label}
        </span>
    );
}

/* -------- Fair vs affordable comparison + CTA -------- */
function AffordVsFairCard({ result, onStartTrial, trialStatus }) {
    const fair = Number(result?.fair_weekly || 0);
    const affordable = Number(result?.affordable_weekly || 0);
    const max = Math.max(fair, affordable, 1);
    const active = trialStatus && (trialStatus.status === "active" || trialStatus.status === "expiring");
    return (
        <section className="rounded-2xl bg-white shadow-sm border border-[#E4E8F0] p-5" data-testid="wc-afford-vs-fair">
            <h2 className="text-[14px] font-bold" style={{ color: NAVY }}>Fair wage vs what the business can afford</h2>
            <div className="mt-4 grid grid-cols-2 gap-4 items-end" style={{ minHeight: 120 }}>
                <Bar value={fair} max={max} color={NAVY} label="Fair wage / wk" sub={`$${fair.toLocaleString("en-NZ", { maximumFractionDigits: 0 })}`} />
                <Bar value={affordable} max={max} color={GREEN} label="Affordable / wk" sub={`$${affordable.toLocaleString("en-NZ", { maximumFractionDigits: 0 })}`} />
            </div>
            <div className="mt-4 space-y-1 text-[13px]">
                <div>Owner subsidy: <b>{nz(result?.owner_subsidy)} a year</b></div>
                <div>Extra revenue needed: <b>{nz(result?.extra_revenue_monthly)} a month</b></div>
                {result?.extra_clients != null && (
                    <div className="text-[12.5px] text-[#5E636E]">= about <b>{result.extra_clients}</b> extra clients at your current average fee</div>
                )}
            </div>
            <button
                onClick={active ? () => toast.info("Full PDF/Excel reports arrive in the next phase.") : onStartTrial}
                className="mt-5 w-full rounded-md py-3 text-white font-semibold text-[14px]"
                style={{ background: GREEN }}
                data-testid="wc-start-trial"
            >
                {active ? "Download my full report" : "Start my free 30 days — see my full report"}
            </button>
            <div className="mt-2 text-center text-[11px] text-[#5E636E]">
                Planning guide only, not tax or financial advice. After 30 days, become an NZ Taxation client to keep using it.
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                    onClick={() => toast.info("Branded PDF export arrives in Phase E.")}
                    className="rounded-md border py-2 text-[12.5px] font-semibold"
                    style={{ borderColor: NAVY, color: NAVY }}
                    data-testid="wc-download-pdf"
                >
                    Download my results (PDF)
                </button>
                <button
                    onClick={() => toast.info("Branded Excel export arrives in Phase E.")}
                    className="rounded-md border py-2 text-[12.5px] font-semibold"
                    style={{ borderColor: NAVY, color: NAVY }}
                    data-testid="wc-download-xlsx"
                >
                    Download Excel
                </button>
            </div>
        </section>
    );
}

function Bar({ value, max, color, label, sub }) {
    const pct = max > 0 ? Math.max(4, (value / max) * 100) : 4;
    return (
        <div className="flex flex-col items-center">
            <div className="w-full flex items-end" style={{ height: 90 }}>
                <div className="w-full rounded-md flex items-start justify-center text-white text-[12px] font-semibold py-1" style={{ background: color, height: `${pct}%` }}>
                    {sub}
                </div>
            </div>
            <div className="mt-1 text-[11.5px] text-[#5E636E]">{label}</div>
        </div>
    );
}

/* -------- Footer -------- */
function FooterTrust() {
    const items = ["No credit card", "Takes 5 minutes", "Your file is never shared", "Plain-English results", "Built for NZ sole traders"];
    return (
        <footer className="bg-white border-t border-[#E4E8F0] px-4 sm:px-6 lg:px-10 py-6">
            <div className="mx-auto max-w-7xl">
                <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-[12.5px]" style={{ color: NAVY }}>
                    {items.map((t) => <span key={t} className="flex items-center gap-1.5"><span style={{ color: GREEN }}>✓</span> <b>{t}</b></span>)}
                </div>
                <div id="contact" className="mt-4 text-center text-[12px] text-[#5E636E]">
                    NZ Taxation Limited · 785A Te Atatu Road, Te Atatu Peninsula, Auckland 0610 · <a href="mailto:info@nztaxation.nz" style={{ color: NAVY }}>info@nztaxation.nz</a> · +64 21 374 007 · <a href="https://www.nztaxation.nz" target="_blank" rel="noreferrer" style={{ color: NAVY }}>www.nztaxation.nz</a>
                </div>
                <div className="mt-2 text-center text-[11px] text-[#5E636E]">
                    <Link to="/privacy" className="underline">Privacy policy</Link>
                </div>
            </div>
        </footer>
    );
}

function UploadIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
    );
}

/* -------- Trial start dialog -------- */
function TrialStartDialog({ onClose }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", business_type: "Sole trader" });
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [emailOk, setEmailOk] = useState(true);
    const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function submit() {
        if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) { toast.error("Fill in name, email and phone."); return; }
        if (!consent) { toast.error("Please tick the Privacy Act consent box."); return; }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/api/public/wage-calculator/trial/start`, {
                name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
                business_type: form.business_type.trim(), consent: true,
            });
            setEmailOk(!!data?.email_sent);
            setSent(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't start the trial — try again.");
        } finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="wc-trial-dialog">
            <div className="w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-4 text-white flex items-start justify-between" style={{ background: `linear-gradient(90deg, ${NAVY} 0%, ${GREEN} 100%)` }}>
                    <div>
                        <div className="text-[11px] uppercase tracking-wider opacity-90">NZ Taxation Limited</div>
                        <div className="text-[18px] font-bold">Start your free 30 days</div>
                        <div className="text-[12px] opacity-90 mt-1">No credit card. Takes 2 minutes. Your file is never shared.</div>
                    </div>
                    <button onClick={onClose} className="text-white/90 hover:text-white" data-testid="wc-trial-close">✕</button>
                </div>
                {sent ? (
                    <div className="p-5 text-center space-y-2">
                        <div className="text-[36px]">{emailOk ? "📩" : "⚠️"}</div>
                        <div className="text-[16px] font-bold" style={{ color: NAVY }}>{emailOk ? "Check your inbox" : "Lead captured — email couldn't be sent"}</div>
                        <div className="text-[13px] text-[#5E636E]">{emailOk ? <>We've sent a verification link to <b>{form.email}</b>. Click it and the 30-day clock starts.</> : <>Your details are saved. Please email <a href="mailto:info@nztaxation.nz" className="underline">info@nztaxation.nz</a> and we'll send the verification link manually.</>}</div>
                        <button onClick={onClose} className="mt-4 rounded-md px-4 py-2 text-white text-[13px] font-semibold" style={{ background: NAVY }}>Got it</button>
                    </div>
                ) : (
                    <div className="p-5 space-y-3">
                        <Field label="Your name"><TrialInput value={form.name} onChange={(v) => patch("name", v)} placeholder="e.g. Sarah Thompson" testid="wc-trial-name" /></Field>
                        <Field label="Email"><TrialInput type="email" value={form.email} onChange={(v) => patch("email", v)} placeholder="you@yourbusiness.co.nz" testid="wc-trial-email" /></Field>
                        <Field label="Phone"><TrialInput value={form.phone} onChange={(v) => patch("phone", v)} placeholder="+64 21 123 4567" testid="wc-trial-phone" /></Field>
                        <Field label="Business type">
                            <select value={form.business_type} onChange={(e) => patch("business_type", e.target.value)}
                                className="w-full h-10 rounded-md border px-2 text-[13px]"
                                style={{ background: CELL, color: CELL_TEXT, borderColor: "#F3E8A2" }}
                                data-testid="wc-trial-business">
                                {["Sole trader", "Partnership", "Company", "Trust", "Other"].map((o) => <option key={o}>{o}</option>)}
                            </select>
                        </Field>
                        <label className="flex items-start gap-2 text-[12.5px] text-[#334155] cursor-pointer" data-testid="wc-trial-consent">
                            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
                            <span>I agree to the <Link to="/privacy" target="_blank" className="underline" style={{ color: NAVY }}>NZ Privacy Act 2020 terms</Link> and to NZ Taxation Limited emailing me about my access.</span>
                        </label>
                        <button onClick={submit} disabled={busy} className="w-full rounded-md py-2.5 text-white font-semibold text-[14px]" style={{ background: GREEN }} data-testid="wc-trial-submit">
                            {busy ? "Sending verification…" : "Start my free 30 days"}
                        </button>
                        <div className="text-center text-[11px] text-[#5E636E]">One trial per email. Planning guide only, not tax advice.</div>
                    </div>
                )}
            </div>
        </div>
    );
}

function TrialInput({ value, onChange, placeholder, type = "text", testid }) {
    return (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
            className="w-full h-10 rounded-md border px-3 text-[13.5px] font-medium outline-none focus:ring-2 focus:ring-[#1F3864]"
            style={{ background: CELL, color: CELL_TEXT, borderColor: "#F3E8A2" }}
            data-testid={testid} />
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-[12px] font-semibold mb-1" style={{ color: NAVY }}>{label}</span>
            {children}
        </label>
    );
}

/* -------- Logged-in-client sync badge -------- */
function SyncBadge({ syncSource, onPull, syncing }) {
    const [months, setMonths] = useState(4);
    const source = (syncSource.source || "").toUpperCase();
    const synced = syncSource.last_synced_at ? new Date(syncSource.last_synced_at).toLocaleString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "never";
    return (
        <section className="rounded-2xl bg-white shadow-sm border border-emerald-200 p-4" data-testid="wc-sync-badge">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 font-bold text-[12px]">{source.slice(0, 2) || "✓"}</span>
                    <div className="min-w-0">
                        <div className="text-[13px] font-semibold" style={{ color: NAVY }}>Source: {source || "Integration"} <span className="font-normal text-[12px] text-[#5E636E]">· last synced {synced}</span></div>
                        <div className="text-[11.5px] text-[#5E636E]">Pre-fill the grid with your actual numbers — nothing is shared outside this calculator.</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 text-[11.5px] text-[#5E636E]">
                        Months
                        <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="h-8 rounded border border-[#CBD5E1] bg-white px-1 text-[12px]" data-testid="wc-sync-months">
                            {[3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </label>
                    <button onClick={() => onPull(months)} disabled={syncing} className="rounded-md px-3 py-1.5 text-[12.5px] font-semibold text-white disabled:opacity-60" style={{ background: GREEN }} data-testid="wc-sync-pull">
                        {syncing ? "Pulling…" : `Use my ${source} data`}
                    </button>
                </div>
            </div>
        </section>
    );
}
